import React, { useRef, useState } from "react"
import { Calendar, ChevronDown, Repeat } from "styled-icons/feather"

import Popper from "components/common/Popper"
import { MenuContainer, MenuItem } from "components/navigation/Menu"
import { bookADemoUrl } from "constants/booking"

import * as styles from "../styles"
import { POSTING_DAYS, describeWeekDistance, formatWeekOf, formatWeekRange, mondayOptions, postingDayLabel, togglePostingDay } from "../planning"
import type { PlanningDefaults, PlanningSettings, PostingDay } from "../planning"

// The week menu is portaled next to the planning popover, so the popover's outside-click detector
// must be told to ignore it; both popovers list this id among their exceptions.
export const PLANNING_WEEK_MENU_ID = "planning-week-menu"
export const PLANNING_MENU_WIDTH = 420
const WEEK_MENU_Z_INDEX = 40

type PlanningControlProps = {
  value: PlanningSettings
  defaults: PlanningDefaults
  onChange: (value: PlanningSettings) => void
  testIdPrefix: string
  disabled?: boolean
}

const PlanningControl: React.FC<PlanningControlProps> = ({ value, defaults, onChange, testIdPrefix, disabled = false }) => {
  const [isWeekMenuOpen, setIsWeekMenuOpen] = useState(false)
  const [showUpgradeHint, setShowUpgradeHint] = useState(false)
  const weekTriggerRef = useRef<HTMLButtonElement>(null)
  const { postsPerWeekAllowed } = defaults

  const weekOptions = mondayOptions(defaults.firstMonday)
  // A running batch may keep a starting week before the earliest selectable one.
  const weeks = weekOptions.includes(value.firstMonday) ? weekOptions : [value.firstMonday, ...weekOptions]

  const handleDayClick = (day: PostingDay) => {
    // On a one-post plan a tap on another day switches the day; that is the moment to offer a second one.
    if (postsPerWeekAllowed === 1 && !value.postingDays.includes(day)) setShowUpgradeHint(true)
    onChange({ ...value, postingDays: togglePostingDay(value.postingDays, day, postsPerWeekAllowed) })
  }

  const handleWeekSelect = (monday: string) => {
    setIsWeekMenuOpen(false)
    onChange({ ...value, firstMonday: monday })
  }

  return (
    <styles.PlanningControlBox data-testid={`${testIdPrefix}-planning`}>
      <styles.PlanningSection>
        <styles.PlanningLabel>
          <Repeat size={13} />
          {postsPerWeekAllowed === 1 ? "Posting day" : "Posting days"}
        </styles.PlanningLabel>
        <styles.PlanningDayChips>
          {POSTING_DAYS.map((day) => {
            const isActive = value.postingDays.includes(day)
            return (
              <styles.PlanningDayChip
                key={day}
                type="button"
                $active={isActive}
                aria-pressed={isActive}
                aria-label={postingDayLabel(day)}
                title={postingDayLabel(day)}
                disabled={disabled}
                onClick={() => handleDayClick(day)}
                data-testid={`${testIdPrefix}-day-${day}`}
              >
                {postingDayLabel(day, "initial")}
              </styles.PlanningDayChip>
            )
          })}
        </styles.PlanningDayChips>
        {postsPerWeekAllowed === 1 && showUpgradeHint && (
          <styles.PlanningUpgradeNotice data-testid={`${testIdPrefix}-upgrade`}>
            Your plan includes one post a week. Post twice a week?{" "}
            <styles.PlanningUpgradeLink href={bookADemoUrl("advanced-planning")} target="_blank" rel="noopener noreferrer">
              Book a call
            </styles.PlanningUpgradeLink>
          </styles.PlanningUpgradeNotice>
        )}
      </styles.PlanningSection>

      <styles.PlanningDivider />

      <styles.PlanningSection>
        <styles.PlanningLabel>
          <Calendar size={13} />
          Starting week
        </styles.PlanningLabel>
        <styles.PlanningWeekTrigger
          ref={weekTriggerRef}
          type="button"
          $open={isWeekMenuOpen}
          aria-haspopup="listbox"
          aria-expanded={isWeekMenuOpen}
          disabled={disabled}
          onClick={() => setIsWeekMenuOpen((open) => !open)}
          data-testid={`${testIdPrefix}-week`}
        >
          <styles.PlanningWeekTitle>{formatWeekOf(value.firstMonday)}</styles.PlanningWeekTitle>
          <styles.PlanningWeekMeta>{describeWeekDistance(value.firstMonday, defaults.firstMonday)}</styles.PlanningWeekMeta>
          <ChevronDown size={16} />
        </styles.PlanningWeekTrigger>
        <Popper
          referenceElement={weekTriggerRef.current}
          visible={isWeekMenuOpen}
          placement="bottom-start"
          onClose={() => setIsWeekMenuOpen(false)}
          exceptions={weekTriggerRef.current ? [weekTriggerRef.current] : []}
          offset={[0, 6]}
          zIndex={WEEK_MENU_Z_INDEX}
        >
          <MenuContainer id={PLANNING_WEEK_MENU_ID} width={weekTriggerRef.current?.offsetWidth}>
            <styles.PlanningWeekMenuList role="listbox" data-testid={`${testIdPrefix}-week-options`}>
              {weeks.map((monday) => (
                <MenuItem
                  key={monday}
                  value={monday}
                  selected={value.firstMonday}
                  label={formatWeekOf(monday)}
                  description={`${formatWeekRange(monday)} · ${describeWeekDistance(monday, defaults.firstMonday)}`}
                  onClick={handleWeekSelect}
                />
              ))}
            </styles.PlanningWeekMenuList>
          </MenuContainer>
        </Popper>
      </styles.PlanningSection>
    </styles.PlanningControlBox>
  )
}

export default PlanningControl
